import { goto } from '$app/navigation';
import { auth } from '$lib/auth.svelte';

// Date-only ("2014-12-17") vs full ISO datetime ("2024-01-22T23:26:34+00:00")
// must be handled differently: `new Date("2014-12-17")` parses as UTC midnight,
// which displays as the previous day in negative-UTC time zones. For date-only
// values we parse as local noon, which renders the intended calendar day
// regardless of viewer time zone or DST.
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

// Note: `type` is intentionally NOT stripped — it's the discriminator on event
// records (EVENT_ATTENDANCE, EVENT_PROMOTION, etc.). It also leaks DynamoDB
// row markers on non-event records (STUDENT, PROGRAM, ...), which is harmless.
const DYNAMO_META = /^(PK|SK|GSI\d+-(?:PK|SK|ID))$/;

function reviveValue(value: unknown): unknown {
  if (typeof value === 'string') {
    if (ISO_DATE_ONLY.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d, 12, 0, 0);
    }
    if (ISO_DATETIME.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  if (Array.isArray(value)) return value.map(reviveValue);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (DYNAMO_META.test(k)) continue;
      out[k] = reviveValue(v);
    }
    return out;
  }
  return value;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public url: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Render an error as a short suffix for toast/UI messages: API message when
 * available, HTTP status as fallback, "network error" for fetch failures. */
export function errorReason(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message || `${err.status}`;
  }
  return 'network error';
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  fetch?: typeof fetch;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `/api${path}`;
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (auth.token) {
    headers['Authorization'] = `Bearer ${auth.token}`;
  }
  const init: RequestInit = {
    method: options.method ?? 'GET',
    signal: options.signal,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined
  };

  const doFetch = options.fetch ?? fetch;
  const response = await doFetch(url, init);

  if (response.status === 401) {
    // Token's gone (expired, revoked, or never had one). Clear the local
    // store and bounce to login. We skip the redirect for all /auth/ endpoints
    // — those pages handle their own 401s (bad credentials, expired token, etc.)
    // and don't need a ?next= redirect layered on top.
    if (!path.startsWith('/auth/')) {
      auth.clear();
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      void goto(`/login?next=${next}`);
    }
    throw new ApiError(response.status, url, `${init.method} ${url} → 401`);
  }

  if (!response.ok) {
    let message = '';
    try {
      const ct = response.headers.get('content-type') ?? '';
      if (ct.includes('application/json')) {
        const body = await response.json();
        message = body?.error?.message ?? '';
      }
    } catch {
      // ignore parse failures — message stays empty, status shown instead
    }
    throw new ApiError(response.status, url, message);
  }

  const contentType = response.headers.get('content-type') ?? '';
  // Read as text first so we never call response.json() on an empty body
  // (Hummingbird emits content-type: application/json with an empty body on
  // some responses, which throws a SyntaxError). Also handles HTTP/2 Lambda
  // responses that omit content-length entirely — we can't predict emptiness
  // from headers alone, so we inspect the body directly instead.
  const body = await response.text();
  if (!contentType.includes('application/json') || !body) {
    return body as T;
  }

  const raw = JSON.parse(body);
  return reviveValue(raw) as T;
}
